import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { forkJoin, of, Subscription } from "rxjs";
import { IPopup } from "../../interfaces/i-popup";
import { BaseViewerService } from "../../services/internal-services/viewer-services/base-viewer.service";
import { IMetadataImage } from "../../interfaces/i-metadataimage";
import { ViewerConstants } from "../../../shared/model/viewer-constants";
import { MatDialog } from "@angular/material/dialog";
import { ImageGalleryService } from "../../services/internal-services/image-gallery/image-gallery.service";
import { UrlStateService } from "../../services/internal-services/url-state.service";
import { MapService } from "../../services/internal-services/map.service";
import { ReportService } from "../../services/internal-services/report.service";
import { GeoService } from "../../services/internal-services/geo.service";
import { Polygon } from "geojson";
import { DialogGalleryComponent } from "../../../dialog-components/image-gallery/dialog-gallery/dialog-gallery.component";
import { GoogleAnalyticsService } from "../../../shared/services/google-analytics.service";
import { VIEWER_SERVICE } from "../../services/internal-services/viewer-services/inject-base-viewer.service";

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: "app-location-info",
  templateUrl: "./location-info.component.html",
  styleUrls: ["./location-info.component.scss"],
  standalone: false,
})
export class LocationInfoComponent implements OnInit, AfterViewInit, OnDestroy {
  private _coordinates: number[];

  private _infoProperty: IPopup;

  pinColor: string = ViewerConstants.PIN_COLOR;

  private _autoRemove: boolean = true;

  get autoRemove(): boolean {
    return this._autoRemove;
  }

  @Input() set autoRemove(value: boolean) {
    this._autoRemove = value;
  }

  public get coordinates(): number[] {
    return this._coordinates;
  }

  public set coordinates(value: number[]) {
    this._coordinates = value;
  }

  @Input() set infoProperty(value: IPopup) {
    this._infoProperty = value;
    if (this._infoProperty.coordinates && this.coordinates !== this._infoProperty.coordinates) {
      // just when the component was initialized before
      this.coordinates = this._infoProperty.coordinates;
      this.initLocationInfo(this.coordinates);
    }
  }

  get infoProperty(): IPopup {
    return this._infoProperty;
  }

  @ViewChild("copiedToolTip", { static: true }) copiedTooltipRef: ElementRef;

  subscriptions: Subscription[] = [];

  @Output() addressEvent = new EventEmitter<string>();

  address: string;

  location: number[];

  viewExtent: Polygon;

  infoLoading = true;

  images: any;

  enoughZoom = true;

  content_copy = ViewerConstants.CONTENT_COPY;

  constructor(
    public dialog: MatDialog,
    private ngZone: NgZone,
    private galleryService: ImageGalleryService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private urlStateService: UrlStateService,
    public mapService: MapService,
    public reportService: ReportService,
    @Inject(VIEWER_SERVICE) public viewerService: BaseViewerService,
  ) {}

  ngOnInit() {
    this.pinColor = this.viewerService.getPinColor();
    this.initLocationInfo(this.coordinates);
  }

  ngAfterViewInit(): void {
    this.subscriptions.push(
      this.viewerService.updateLocationInfo$.subscribe((infoPopup: IPopup) => {
        if (infoPopup && this.coordinates) {
          this.pinColor = this.viewerService.getPinColor();
          this.infoProperty.selected = this.infoProperty.featuresData
            ? this.infoProperty.featuresData[0].selected
            : this.infoProperty.selected;
          if (this.infoProperty.selected) {
            this.updatePin();
          }
        }
      }),
    );
    this.subscriptions.push(
      this.viewerService.zoomEnd$.subscribe((event: any) => {
        if (event !== null && event.zoom != null) {
          this.enoughZoom = event.zoom >= 17;
        }
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
  }

  clipToClipboard(): string {
    return `${this.address}\n${this.location[1].toPrecision(6)}, ${this.location[0].toPrecision(6)}`;
  }

  setLocationInfoPopUp(address: string, images: any) {
    this.ngZone.run(() => {
      this.address = address;
      this.addressEvent.emit(address);
      this.location = this.coordinates;
      this.images = images;
    });
  }

  updatePin() {
    this.viewerService.getCoreMap().removeRedPinMarkers();
    this.viewerService.getCoreMap().showRedPinMarker(
      this.coordinates,
      ViewerConstants.PROJECTIONS.WGS84,
      {
        icon: {
          src: "assets/images/small_pin.svg",
          color: this.pinColor,
        },
      },
      this.infoProperty.id,
    );
  }

  openImageGalleryDialog(): void {
    this.galleryService.setDisplayGallery(true);
    this.dialog.open(DialogGalleryComponent, {
      width: "80%",
      height: "75%",
      panelClass: "gallery-dialog",
      disableClose: true,
      data: {
        viewerService: this.viewerService,
        googleAnalyticsService: this.googleAnalyticsService,
        galleryService: this.galleryService,
        images: this.images,
        center: this.location,
        viewExtent: this.viewExtent,
        address: this.address,
      },
    });
  }

  handleLocationInfo(allImages: IMetadataImage[], address: string, init = false) {
    this.setLocationInfoPopUp(address, allImages.length ? allImages : null);
    if (init) this.urlStateService.updateQueryParams({ address });
  }

  /**
   * Init info for showing info dialog
   * @param coordinates in wgs84
   */
  initLocationInfo(coordinates: number[]) {
    this.location = coordinates;
    this.viewExtent = GeoService.toWgs84(this.mapService.getMapStatus().extent.geometry);
    const sources = [
      of(coordinates),
      GeoService.reverseGeocode([coordinates[1], coordinates[0]]),
      this.viewerService.getAllAvailableImages([...coordinates]),
    ];
    forkJoin(sources).subscribe(
      (result: any[]) => {
        const address = result[1];
        const allAvailableImages = result[2];

        this.handleLocationInfo(allAvailableImages, address[0].formatted_address, true);
        this.infoLoading = false;
      },
      (error) => {
        if (error === ViewerConstants.UNAUTHORIZED_ERROR) {
          this.viewerService.setUnauthorized();
          this.infoLoading = false;
        }
      },
    );
  }

  removeInfoProperties() {
    this.viewerService.removeInfoProperty(this.infoProperty);
    this.viewerService.getCoreMap().removeRedPinMarker(this.infoProperty.id);
  }

  showPin() {
    // close before/after if is opened
    if (
      this.mapService.swipeStatus.enable &&
      !GeoService.isCenterEquals(this.viewerService.getCenterCoordinates(), this.coordinates)
    ) {
      this.viewerService.searchEnd({ search: true });
    }
    this.mapService.setCenter([this.coordinates[0], this.coordinates[1]]);
    this.viewerService.setCenterCoordinates([this.coordinates[0], this.coordinates[1]]);
    // this.viewerService.showNewestProject(); // newest now is always on top
    this.updatePin();
    this.viewerService.setSelectedFeaturesStyle(this.infoProperty.featuresData);

    this.viewerService.getSelectedFeatures()?.forEach((f, index) => {
      const id = this.infoProperty.featuresData ? this.infoProperty.featuresData[index].id : this.infoProperty.id;
      f.selected = f.id === id;
    });
    const infoProperties = this.viewerService.getInfoProperties();

    infoProperties.forEach((ip) => {
      ip.featuresData?.forEach((feature) => {
        feature.selected = ip.id === this.infoProperty.id;
      });
      ip.selected = ip.id === this.infoProperty.id;
    });
    if (infoProperties.indexOf(this.infoProperty) !== -1) {
      this.viewerService.setInfoProperties(infoProperties);
    }

    this.urlStateService.updateQueryParams({ address: this.address });
  }
}
